/**
 * Spanish copy — the reference catalogue.
 *
 * The wording carries the reframe the whole instrument depends on: this is a
 * maturity ladder, not a frequency scale. The rater's act is to *place*
 * someone, never to grade them. Keep verbs like "ubicar" and "reconocer";
 * avoid "calificar", "puntuar" and "¿qué tan seguido...?".
 */
export const es = {
  common: {
    continue: "Continuar",
    back: "Atrás",
    finish: "Terminar",
    start: "Comenzar",
    save: "Guardar",
    edit: "Editar",
    remove: "Quitar",
    cancel: "Cancelar",
    loading: "Cargando…",
    saving: "Enviando…",
    retry: "Reintentar",
    of: "de",
  },

  scale: {
    heading: "Los tres niveles de madurez",
    // The question that defines the instrument.
    prompt: "¿En cuál de estos niveles ubicas hoy a {name}?",
    promptSelf: "¿En cuál de estos niveles te ubicas hoy?",
    step1: "Latente",
    step3: "Consistente",
    step5: "Referente",
    transition: "En transición",
    transitionFrom: "En transición ({from} → {to})",
    notObserved: "No lo he podido observar",
    notObservedHint: "— esta conducta no me consta",
    lowEnd: "apenas asoma",
    highEnd: "pleno",
    placeholder: "Toca un número en la escala para responder.",
    yourAnswer: "Tu respuesta",
  },

  pillars: {
    DEBATE: "Debate",
    DECIDE: "Decide",
    DELIVER: "Deliver",
    DEBATE_motto: "crea el mejor pensamiento",
    DECIDE_motto: "crea claridad y compromiso",
    DELIVER_motto: "crea impacto sostenible",
  },

  coaches: {
    title: "Coaches",
    subtitle: "{count} coaches en el programa",
    none: "Todavía no hay coaches. Crea el primero.",
    new: "Crear coach",
    newTitle: "Nuevo coach",
    create: "Crear",
    coacheeCount: "{count} asignados",
    createHelp:
      "Solo crea la cuenta. Después, desde el listado, le envías la invitación con su acceso.",
    editHelp: "Cambiar el correo cambia a dónde va la invitación.",
    editHelpSignedIn:
      "Cambiar el correo cambia a dónde va la invitación y con cuál entra: esta cuenta ya tiene contraseña, así que a partir de ahora entra con la dirección nueva.",
    sendInvitation: "Enviar invitación",
    resend: "Reenviar",
    resendConfirm: "¿Seguro? Le cambia la contraseña",
    notInvited: "sin invitar",
    invitedOn: "invitado el {date}",
    hasEntered: "ya entró",
    footer:
      "Solo un admin ve y edita esta sección: la lista de asignados es todo lo que un coach puede ver.",
    footerReadOnly:
      "Solo lectura: quién acompaña a quién. Crear coaches y asignar evaluados es cosa del admin.",
    field: {
      fullName: "Nombre y apellido",
      email: "Correo",
      phone: "Teléfono (opcional)",
      password: "Contraseña",
    },
    coacheesTitle: "A quién acompaña",
    coacheesLead:
      "Es lo único que verá al entrar: su listado de evaluados y sus reportes.",
    searchPlaceholder: "Buscar un evaluado por nombre o cargo…",
    noCoachees: "Sin nadie asignado. Al entrar verá una lista vacía.",
    assignmentFooter:
      "Cada evaluado tiene un solo coach: asignar a alguien que ya tiene otro lo traspasa.",
  },

  staff: {
    title: "Equipo P&O",
    subtitle: "{count} cuentas del cliente",
    none: "Todavía no hay cuentas de P&O.",
    new: "Crear cuenta",
    createHelp:
      "Solo crea la cuenta. Después, desde el listado, le envías la invitación con su acceso.",
    footer:
      "Acceso de solo lectura: ven el avance, los evaluados, los evaluadores y los coaches. No envían invitaciones ni gestionan a nadie.",
  },

  roleLevels: {
    SELF_LEADER: "Self Leader",
    PEOPLE_LEADER: "People Leader",
    AREA_LEADER: "Area Leader",
  },

  relationships: {
    SELF: "Autoevaluación",
    SUPERVISOR: "Supervisor",
    PEER: "Pares",
    DIRECT_REPORT: "Reportes directos",
    STAKEHOLDER: "Stakeholders",
  },

  // How a screen of the multiple format is introduced, from the rater's side:
  // "SUPERVISOR" means the rater supervises these people.
  evaluatingGroup: {
    SUPERVISOR: "Personas que supervisas",
    DIRECT_REPORT: "Tu supervisor",
    PEER: "Tus pares",
    STAKEHOLDER: "Stakeholders",
    SELF: "Tu autoevaluación",
  },

  survey: {
    greeting: "Hola, {name}.",
    // The self-assessment speaks to the reader about themselves. Everything
    // else in the survey is written in the third person about someone else.
    introTitleSelf: "Tu propia mirada, primero.",
    introLeadSelf:
      "Antes de recibir el feedback de quienes trabajan contigo, te pedimos tu propia mirada. Para cada comportamiento lees los tres niveles de madurez y eliges dónde te ubicas hoy.",
    selfPurpose:
      "Tu autoevaluación se compara con la mirada de los demás en tu reporte. Ahí es donde aparece lo interesante: dónde coinciden y dónde no.",
    selfStepChooseBody: "Elige el nivel que más se parece a cómo trabajas hoy.",
    selfStepObservedBody:
      '¿Un comportamiento que no te ha tocado ejercer? Elige "No lo he podido observar".',
    doneTitleSelf: "Gracias por tu mirada sobre ti.",
    doneTravellingSelf:
      "Cuando el feedback de los demás esté completo, un coach de Genoma preparará tu reporte y lo revisará antes de compartirlo contigo.",
    introTitleIndividual: "Una nueva forma de dar feedback.",
    introTitleMultiple: "Evalúa por comportamiento.",
    evaluating: "Estás evaluando a {name}",
    settleIn:
      "Antes de empezar, ponte en un lugar cómodo y sin prisa. Vas a pensar en personas concretas, y eso se hace mejor con calma.",
    settleInSelf:
      "Antes de empezar, ponte en un lugar cómodo y sin prisa. Mirarse a uno mismo con honestidad se hace mejor sin apuro.",
    timeEstimate: "Te va a tomar unos {minutes} minutos.",
    timeEstimateHelp:
      "No hace falta que lo hagas de una sola vez: guardamos tu avance y puedes retomar desde el mismo enlace cuando quieras.",
    deviceHint:
      "Te recomendamos hacerlo desde una laptop o computador, y en Google Chrome. Se ve bastante mejor que en el móvil, sobre todo cuando evalúas a varias personas a la vez.",
    deviceHintSelf:
      "Te recomendamos hacerlo desde una laptop o computador, y en Google Chrome. Los tres niveles se leen bastante mejor que en el móvil.",
    introLeadIndividual:
      "Para cada comportamiento lees los tres niveles de madurez y eliges en la escala el que más se parece a lo que has visto. Sin notas, sin juzgar — solo reconocer.",
    introLeadMultiple:
      "Te pedimos tu mirada sobre {count} líderes con quienes trabajas. Puedes hacerlo por partes — guardamos tu avance y retomas cuando quieras.",
    stepReadTitle: "Lee los tres niveles.",
    stepReadBody:
      "Latente, Consistente, Referente — el diferenciador va en negrita.",
    stepChooseTitle: "Elige en la escala 1–5.",
    stepChooseBody: "El 2 y el 4 son pasos intermedios, entre un nivel y otro.",
    stepWriteTitle: "Al final, una pregunta abierta.",
    stepWriteBody:
      "Después de los 12 comportamientos te pediremos que escribas, en tus palabras, qué aporta cada persona y qué le ayudaría a crecer. Es opcional, y es lo que más se agradece.",
    selfStepWriteBody:
      "Al final te pediremos que escribas, en tus palabras, qué aportas y qué te ayudaría a crecer. Es opcional, y es con lo que empieza tu conversación de coaching.",
    stepObservedTitle: "Responde solo lo que has visto.",
    stepObservedBody: "¿No te consta? Elige “No lo he podido observar”.",
    anonymityNotice:
      "Tus respuestas son anónimas. Nadie ve una respuesta individual.",
    // Shown instead of the notice above when the rater is the leader's manager:
    // their line is displayed on its own, so calling it anonymous would be false.
    anonymityNoticeSupervisor:
      "Cuando evalúas a alguien de quien eres supervisor, tu valoración se muestra por separado en su reporte, identificada como la del supervisor. Las respuestas de pares, reportes directos y stakeholders son anónimas y se combinan entre sí.",
    anonymityPurpose: "Sirven para que {name} crezca, no para calificarla.",
    progress: "{current} / {total}",
    nextGroup: "Siguiente grupo de personas →",
    groupHeading: "{group} · nivel {level}",
    missingTitle: "Te faltan {count} respuestas",
    missingBody:
      "Te trajimos a la primera pantalla que quedó incompleta. Complétala y sigue adelante — el resto de tus respuestas está guardado.",
    anchorsChangedTitle: "Ojo: cambian las descripciones",
    anchorsChangedBody:
      "Estas personas son {level}, así que los tres niveles describen ahora conductas distintas. Vuelve a leerlos antes de responder.",
    nextBehavior: "Siguiente comportamiento →",
    advancing: "Siguiente…",
    nextComments: "Última pregunta →",
    savedAutomatically: "Guardamos tu avance automáticamente.",
    doneTitle: "Gracias por tu mirada.",
    doneAnswered: "Respondiste {answered} comportamientos.",
    doneNotObserved: "Marcaste {count} como no observados.",
    doneTravelling:
      "Tu feedback ya viaja —anónimo— al reporte de {name}. Un coach de Genoma lo revisará antes de compartirlo.",
    doneTravellingMany:
      "Tu feedback ya viaja —anónimo— al reporte de cada persona. Un coach de Genoma los revisará antes de compartirlos.",
    alreadyDoneTitle: "Ya completaste esta encuesta.",
    alreadyDoneHelp:
      "No hace falta que hagas nada más. Si crees que deberías poder cambiar una respuesta, escribe a quien te invitó.",
    resumeNotice: "Retomamos donde lo dejaste.",
    linkExpired: "Este enlace caducó.",
    linkExpiredHelp:
      "Pide uno nuevo a la persona que te invitó y lo reenviamos.",
  },

  nav: {
    raters: "Evaluadores",
    coaches: "Coaches",
    staff: "Equipo P&O",
    overview: "Resumen",
    myReport: "Mi reporte",
    participants: "Evaluados",
  },

  roles: {
    ADMIN: "Admin",
    HR: "P&O",
    COACH: "Coach",
    PARTICIPANT: "Participante",
    RATER: "Evaluador",
  },

  participants: {
    noAccessYet: "sin acceso",
    title: "Evaluados",
    count: "{shown} de {total}",
    searchPlaceholder: "Buscar por nombre, cargo o área…",
    allAreas: "Todas las áreas",
    allLevels: "Todos los niveles",
    noMatches: "Nadie coincide con esa búsqueda.",
    whoAnswered: "Quién ha respondido",
    expandHelp: "Toca un grupo para ver quiénes lo componen.",
    foldsIntoGlobal: "va al global",
    thresholdHelpFull:
      "Un segmento se muestra por separado solo si responden al menos {min} personas; por debajo de eso sus respuestas se combinan en el promedio global. La marca en la barra señala ese mínimo.",
    raterSubmitted: "Enviada",
    raterInProgress: "En curso",
    raterPending: "Pendiente",
    thresholdHelp:
      "La marca en cada barra es el mínimo de {min} personas para que ese segmento se muestre por separado.",
    selfAssessment: "Autoevaluación",
    outlookBlockedHelp:
      "Todavía no hay suficientes respuestas para generar un reporte. Hacen falta al menos 3 personas.",
    outlookAtRiskHelp:
      "Estos segmentos aún no llegan al mínimo y podrían lograrlo si se les recuerda: {segments}.",
    outlookGlobalHelp:
      "El reporte mostrará un único promedio global, sin desglose por segmento.",
    outlookSegmentedHelp: "El reporte podrá mostrar el desglose por segmento.",
    reportNotBuilt: "Sin generar",
    reportNotApproved: "Borrador · aún no aprobado",
    reportNotApprovedHelp:
      "Los números ya se calculan con las respuestas recibidas. Falta que un coach lo revise y lo apruebe.",
    openReport: "Ver reporte",
    reportNotBuiltHelp:
      "El reporte se genera al cerrar el ciclo, lo revisa un coach y solo entonces se publica. Esa pantalla es lo siguiente que vamos a construir.",
  },

  auth: {
    signIn: "Entrar",
    email: "Correo electrónico",
    password: "Contraseña",
    showPassword: "Mostrar contraseña",
    hidePassword: "Ocultar contraseña",
    sendLink: "Enviarme un enlace de acceso",
    linkSent:
      "Si esa dirección tiene una cuenta, te enviamos un enlace de acceso.",
    signOut: "Salir",
    invalidCredentials: "Correo o contraseña incorrectos.",
  },

  dashboard: {
    seeAllAlerts: "Ver las {count} restantes",
    alertsTitle: "Alertas operativas",
    alertsLead:
      "Lo que hay que resolver para que los reportes salgan completos.",
    startedNotSent: "{count} sin enviar",
    title: "Avance del proceso",
    subtitle: "Vista operativa · no confidencial",
    closesIn: "cierra en {days} días",
    closeDueToday: "el cierre vence hoy",
    closeOverdue: "el cierre venció hace {days} días",
    evaluated: "Evaluados",
    invitedRaters: "Evaluadores invitados",
    invitedRatersHelp: "{pairs} evaluaciones en total",
    completedOf: "{completed} de {invited} han completado su encuesta",
    reportsApproved: "Reportes aprobados",
    reportsApprovedHelp: "Aprobados por un coach tras revisar el cálculo.",
    responseRate: "Participación",
    ofInvited: "{responded} de {invited} evaluadores han respondido",
    selfAssessments: "Autoevaluaciones",
    notStarted: "Sin empezar",
    notStartedHelp: "Encuestas que nadie ha abierto todavía.",
    byParticipant: "Actividad reciente",
    seeAll: "Ver todos",
    byRelationship: "Por relación",
    participationTitle: "Participación 360",
    legendDone: "recibidas",
    legendShare: "% total",
    responsesReceived: "Evaluaciones recibidas",
    pendingUnit: "evaluaciones",
    totalAssessments: "Total de evaluaciones",
    segmentHelp:
      "Un segmento se muestra por separado en el reporte solo si responden al menos {min} personas.",
    colName: "Evaluado",
    colParticipation: "Participación",
    colSelf: "Autoevaluación",
    colOutlook: "Reporte hoy",
    colReport: "Estado",
    keyLeader: "clave",
    done: "Hecha",
    pending: "Pendiente",
    outlookBlocked: "No alcanza",
    outlookGlobalOnly: "Solo global",
    outlookSegmented: "Segmentado",
    notGenerated: "Sin generar",
    participation: "Participación",
    alerts: "Alertas operativas",
    noAlerts: "Sin alertas. El proceso avanza según lo previsto.",
    privacyFooter:
      "Las respuestas individuales y los comentarios cualitativos nunca se muestran en esta vista.",
    segmentFolded:
      "Algunos segmentos tienen menos de 3 respuestas y se muestran dentro del global por confidencialidad.",
  },

  alertSeverity: {
    CRITICAL: "Crítico",
    WARNING: "Atención",
    INFO: "Info",
  },

  alerts: {
    reportBlocked:
      "El reporte de {name} no se puede generar: solo {count} persona(s) han respondido.",
    segmentAtRisk:
      "A {name} le faltan respuestas para poder segmentar: {segments}.",
    keyLeaderDelayed: "{name} es líder clave y va al {rate}% de participación.",
    selfAssessmentMissing:
      "A {name} ya le respondieron todos, pero no ha enviado su autoevaluación.",
    lowParticipation: "{count} evaluados por debajo del 60% de participación.",
    raterNotStarted: "{count} evaluadores no han abierto su encuesta.",
    reportAwaitingApproval:
      "{count} reportes esperan la aprobación de un coach.",
    surveyReadyToClose: "Todos respondieron. El ciclo se puede cerrar.",
    closingSoon: "El ciclo cierra en {count} días.",
    closeDueToday:
      "El ciclo cierra hoy y sigue abierto. Ciérralo o mueve la fecha.",
    closeOverdue:
      "La fecha de cierre pasó hace {count} días y el ciclo sigue abierto. Ciérralo o mueve la fecha.",
  },

  reportStatus: {
    DRAFT: "Borrador",
    IN_REVIEW: "En revisión",
    APPROVED: "Aprobado",
    PUBLISHED: "Publicado",
  },

  // The open question. Everything else in the instrument is a number; this is
  // the one place a rater writes, and the report treats what they write more
  // carefully than it treats any score.
  comments: {
    kicker: "En tus palabras",
    title: "Una última pregunta, sin escalas",
    titleSelf: "Una última pregunta, sobre ti",
    lead: "Los números dicen dónde está cada persona. Esto dice por qué, y es lo que más le sirve a quien lo recibe.",
    leadSelf:
      "Los números dicen dónde te ubicas. Esto dice por qué, y es con lo que empieza tu conversación de coaching.",
    prompt:
      "¿Qué es lo que más aporta {name} como líder —en su trabajo, con su equipo o en su área— y qué le ayudaría a crecer?",
    promptSelf:
      "¿Qué es lo que más aportas como líder —en tu trabajo, con tu equipo o en tu área— y qué te ayudaría a crecer?",
    promptHelp:
      "Ilústralo con ejemplos reales; escribe con la libertad y la extensión que quieras.",
    placeholder: "Lo que has visto hacer a {name}…",
    placeholderSelf: "Lo que has visto en ti este año…",
    privacyPooled:
      "Lo que escribas llega sin tu nombre y mezclado con el de las demás personas que respondieron: no se indica de qué grupo viene cada texto. Si menos de tres personas escriben, no se muestra ninguno.",
    privacyAttributed:
      "Como supervisor o supervisora, tu respuesta se muestra identificada como tal. Es la única que se presenta así, y quien la recibe lo sabe.",
    privacySelf: "Esto lo lee tu coach, y queda en tu propio reporte.",
    tooShort: "Faltan {count} palabra(s) para que cuente como respuesta.",
    requiredOne: "Hace falta responderla para poder enviar la encuesta.",
    progress:
      "Has escrito sobre {written} de {total} personas. Hacen falta todas para poder enviar.",
    stillMissing: "Faltan {count} por escribir",
  },

  // The synthesis that stands in for the comments themselves.
  summary: {
    strengths: "Lo que ya haces bien",
    growth: "Lo que te ayudaría a crecer",
    divergences: "Donde no todos coinciden",
    divergencesLead:
      "Las miradas que se contradicen suelen ser lo más útil de un 360: señalan que algo se ve distinto según desde dónde se te mire.",
    method:
      "Esta lectura reúne lo que escribieron {count} personas sobre ti, y la revisó tu coach. No es una cita de nadie: los textos originales no se muestran, porque una frase concreta identifica a quien la escribió mucho más rápido que un promedio.",
    methodCoach:
      "Esta lectura la redactó una IA a partir de {count} respuestas escritas, y la revisas tú antes de aprobarla. La persona evaluada no ve los textos originales: una frase concreta identifica a quien la escribió mucho más rápido que un promedio.",
    notGenerated: "Tu coach todavía no ha preparado esta lectura.",
    notGeneratedCoach:
      "Aún no has generado la lectura cualitativa. Se redacta a partir de las respuestas escritas y la puedes revisar y editar antes de aprobarla.",
    withheld:
      "Escribieron {count} persona(s). No se muestra ni el texto ni una lectura de él: hacen falta al menos {min} respuestas para poder decir algo sin que se deduzca quién lo escribió.",
    notGeneratedReviewer:
      "Todavía no se ha generado la lectura cualitativa. La prepara y la aprueba un administrador; cuando esté, la verás aquí junto a las respuestas originales.",
    methodReviewer:
      "Esta lectura la redactó una IA a partir de {count} respuestas escritas, y la revisa y aprueba un administrador antes de que llegue a la persona evaluada. Tú la lees con las respuestas originales al lado. Quien fue evaluado no ve esos textos: una frase concreta identifica a quien la escribió mucho más rápido que un promedio.",
    notGeneratedHR:
      "Todavía no hay una lectura cualitativa aprobada para esta persona.",
    methodHR:
      "Esta lectura la redactó una IA a partir de las respuestas escritas, y la aprobó el coach de esta persona. Los textos originales no se muestran en P&O: son de quien los escribió y de la conversación de coaching.",
    noneWritten: "Nadie escribió en la pregunta abierta.",
    coachOnly: "Solo para el coach",
    provenance: "Redactada por {model} a partir de {count} respuestas.",
    edited: "editada por un coach",
    stale: "generada con una versión anterior del prompt",
    generate: "Generar lectura",
    regenerate: "Volver a generar",
    working: "Redactando…",
    approve: "Aprobar",
    showSources: "Ver las {count} respuestas originales",
    hideSources: "Ocultar las respuestas originales",
    sourcesNotice:
      "Estas son las respuestas tal cual se escribieron. Solo tú las ves: no están en la copia que recibe la persona evaluada. Están aquí para que puedas comprobar que la lectura dice lo que la gente dijo.",
    addTheme: "Añadir tema",
    themeLabel: "Título",
    detailLabel: "Texto",
    emptySection:
      "Esta sección está vacía. Puedes añadir un tema o dejarla así.",
    lastStrength:
      "No se puede quitar la última fortaleza: una lectura sin ninguna no se puede aprobar.",
    editUnapproves:
      "Al guardar, la lectura vuelve a quedar sin aprobar: tendrás que leerla y aprobarla de nuevo.",
    tooLong:
      "Se ha pasado de largo. El título admite 80 caracteres y el texto 900.",
    essenceTooShort: "El párrafo de apertura necesita al menos 40 caracteres.",
    status: {
      GENERATED: "Borrador sin revisar",
      EDITED: "Editada, sin aprobar",
      APPROVED: "Aprobada",
    },
  },

  // Where a person's links go, and whether any have been sent.
  invitations: {
    reportAccess: "Acceso a su reporte",
    reportAccessHelp:
      "Le envía usuario y contraseña para entrar a leer su propio reporte. No es el enlace de la encuesta.",
    reportAccessSent:
      "Enviado. Solo verá su propio reporte, y solo si ya está aprobado.",
    sendReportAccess: "Enviar acceso",
    language: "Idioma de la encuesta",
    languageHelp: "En qué idioma la ve esta persona.",
    languageDefault: "Español (por defecto)",
    title: "Invitaciones",
    email: "Correo",
    noEmail: "Sin correo todavía",
    addressMovedNeedsResend:
      "Dirección actualizada. Los enlaces enviados a la anterior ya no funcionan ({revoked}), y a la nueva todavía no se ha enviado nada: hay que enviarle la invitación. Lo que ya haya respondido se conserva.",
    noEmailReadOnly:
      "Sin correo todavía. P&O puede añadir uno para poder invitarle.",
    noEmailHelp:
      "El mapa 360 llegó sin direcciones. Pon una para poder enviarle su enlace.",
    none: "Esta persona no tiene ninguna encuesta en este ciclo.",
    selfSurvey: "Su autoevaluación",
    othersSurvey: "Evalúa a {count} persona(s)",
    neverSent: "Nunca se le ha enviado.",
    sentOn: "Enviada {times} vez(ces) · última el {when}",
    sentTimes: "Enviada {times} vez(ces)",
    completed: "Ya la completó.",
    send: "Enviar invitación",
    resend: "Reenviar",
    sending: "Enviando…",
    sentOk: "Enviada. El enlace anterior sigue siendo válido, por si abre ese.",
  },

  // Everyone doing the assessing.
  invitationRun: {
    title: "Invitaciones pendientes de enviar",
    leadIdle:
      "{count} personas no han recibido su invitación ni una vez. Se envían de una en una, cada {seconds} segundos, para que no lleguen todas juntas y acaben en spam: unos {eta}.",
    leadRunning: "Enviando: {sent} de {total}. Quedan unos {eta}.",
    leadDone: "Listo. Se enviaron {sent} invitaciones.",
    start: "Enviar {count} invitaciones",
    confirm: "Sí, enviar las {count}",
    confirmNote:
      "Se enviarán {count} correos a lo largo de unos {eta}. Puedes cerrar esta pestaña: el envío sigue en el servidor. Nadie recibirá una segunda invitación.",
    stop: "Detener",
    counts:
      "{sent} enviadas · {failed} fallidas · {skipped} omitidas · de {total}",
    sendingTo: "enviando a {name}",
    skippedNote:
      "{count} se omitieron porque alguien ya les había enviado su enlace mientras corría el envío.",
    minutes: "{count} min",
    underAMinute: "menos de un minuto",
    almostDone: "un momento",
  },
  raters: {
    title: "Evaluadores invitados",
    subtitle: "{total} personas participan evaluando",
    pendingInvitations: "{count} pendientes sin invitar",
    searchPlaceholder: "Buscar por nombre, correo o área…",
    colPerson: "Persona",
    colEmail: "Correo",
    new: "Crear evaluador",
    create: "Crear",
    createHelp:
      "Solo crea a la persona. Después, desde su ficha, le asignas a quién evalúa — y recién entonces se le puede enviar la invitación.",
    hint: {
      fullName: "Ana Martínez",
      email: "amtz@novonordisk.com",
      jobTitle: "BASAC Procurement Sr Manager",
      area: "F&O, MM&R, LECQ, CA, P&O…",
    },
    field: {
      fullName: "Nombre y apellido",
      email: "Correo",
      jobTitle: "Cargo (opcional)",
      area: "Área (opcional)",
    },
    addPlaceholder: "Agregar a esta lista: busca por nombre, cargo o código…",
    addBlocked:
      "Ya empezó su encuesta, así que su lista queda fija: sus pantallas se armaron con estas personas.",
    addTitle: "¿Agregar a esta persona a su lista?",
    addBody: "{rater} pasará a evaluar también a {name}.",
    addRelationship: "Relación con esa persona",
    addRelationshipHelp:
      "Decide en qué grupo se suman sus respuestas dentro del reporte, así que conviene que sea la real.",
    addConfirm: "Agregar",
    remove: "Quitar",
    removeBlocked:
      "Ya empezó a evaluar a esta persona: sus respuestas no se pueden descartar desde aquí.",
    removeTitle: "¿Quitar a esta persona de su lista?",
    removeBody:
      "Dejará de tener que evaluar a {name}. Su encuesta se acorta y el reporte de {name} contará con un evaluador menos.",
    removeNote:
      "Solo se puede mientras no haya respondido nada sobre esa persona. Queda registrado en el log de auditoría.",
    removeConfirm: "Quitar",
    colAssigned: "Evalúa a",
    colProgress: "Progreso",
    progressLabel: "{percent}% de la encuesta",
    colInvitation: "Invitación",
    noEmail: "Sin correo",
    noEmailShort: "Sin correo todavía",
    notInvited: "Sin invitar",
    invited: "Enviada ×{times}",
    noMatches: "Nadie coincide con esa búsqueda.",
    noJobTitle: "Sin cargo registrado",
    footerCanInvite:
      "Toca una persona para ver a quiénes evalúa, corregir su correo y enviarle su enlace.",
    footerReadOnly:
      "Toca una persona para ver a quiénes evalúa. Corregir correos y enviar invitaciones es de P&O.",
    assessesHeading: "Evalúa a {count} persona(s)",
    assessesLead: "Ha terminado con {done} de {total}.",
    doneButUnsent:
      "Lo ha respondido todo, pero aún no ha pulsado enviar: hasta que lo haga, sus respuestas no entran en ningún reporte.",
    answeredAll: "Completa",
    // Su propio estado, no un caso de "casi". Tiene las doce valoraciones
    // puestas y no puede enviar: lo que le falta es escribir, no puntuar, y
    // llamarlo «completa» era lo que dejaba a esta persona indistinguible de
    // quien terminó.
    answeredNoComment: "Falta su comentario",
    answeredSome: "{answered} de {total}",
    answeredNone: "Sin empezar",
    privacyFooter:
      "Aquí se ve cuánto ha avanzado cada quien, nunca qué respondió. Las respuestas individuales no se muestran en ninguna vista.",
  },

  // The switch that decides whether the surveys accept answers.
  cycle: {
    picker: "Ciclo",
    label: "Encuestas",
    statusOpen: "Abiertas",
    statusClosed: "Cerradas",
    open: "Abrir encuestas",
    close: "Cerrar encuestas",
    archived: "Ciclo archivado",
    openHelp: "{submitted} de {raters} evaluadores han terminado",
    closedHelp: "Nadie puede responder todavía.",
    doOpen: "Sí, abrir",
    doClose: "Sí, cerrar",
    working: "Un momento…",
    confirmOpen:
      "Se abren para {raters} personas y el mapa 360 ya no se podrá reimportar sin descartar lo que respondan.",
    confirmClose:
      "Quedan {pending} personas sin terminar; dejarán de poder hacerlo. Las {responses} respuestas ya recibidas se conservan.",
  },

  report: {
    title: "Reporte de Feedback 360°",
    preparedFor: "Preparado para",
    confidential: "Confidencial",
    notApprovedYet: "Aún no aprobado",
    download: "Descargar PDF",
    downloadHint:
      "Se abre el diálogo de impresión: elige «Guardar como PDF» como destino.",
    kickerAbout: "Sobre este reporte",
    aboutTitle: "Antes de empezar",
    aboutLead:
      "{name}, este reporte es un espejo: reúne cómo te ves tú y cómo te viven quienes trabajan contigo, en los 12 comportamientos del modelo Debate · Decide · Deliver. No es una calificación — es material para crecer. Las diferencias entre tu mirada y la de los demás son lo más valioso para tu conversación de coaching.",
    rung1: "Apenas se manifiesta.",
    rung3: "Se manifiesta con regularidad.",
    rung5: "Emerge incluso en lo difícil; marca la pauta.",
    kickerQuantitative: "Cuantitativo",
    behaviorsTitle: "Resultados por comportamiento",
    behaviorsLead:
      'Tu perfil de madurez en los 12 comportamientos. Por defecto ves tu autoevaluación frente al global; cambia cualquier serie o elige "Ninguno" para ver un solo grupo.',
    series: {
      SELF: "Tu autoevaluación",
      GLOBAL: "Global (todos los que te evaluaron)",
      SUPERVISOR: "Supervisor",
      PEER: "Pares",
      DIRECT_REPORT: "Reportes directos",
      STAKEHOLDER: "Stakeholders",
    },
    seriesA: "Serie A",
    seriesB: "Serie B",
    seriesNone: "Ninguno (ver solo A)",
    seriesUnavailable: "no disponible",
    seriesReason: {
      BELOW_MINIMUM: "muy pocas respuestas para mostrarlo aparte",
      NO_RESPONSES: "sin respuestas",
      TOO_FEW_SEGMENTS:
        "se pliega al global para no dejar identificable a otro grupo",
      MANUAL_OVERRIDE: "plegado a mano",
      NOT_DISCLOSED: "no se muestra por separado",
    },
    versus: "vs",
    foldedNotice:
      "Estos grupos no se muestran por separado porque respondieron menos de {min} personas: {segments}. Sus respuestas sí están dentro del promedio global.",
    kickerInterpretation: "Interpretación",
    interpretationTitle: "Fortalezas y áreas de mejora",
    strengths: "Tus puntos fuertes",
    strengthsLead:
      "Donde los demás te ven más alto — tu base para apalancarte.",
    development: "Áreas de mejora",
    developmentLead: "Los comportamientos con más recorrido por delante.",
    blindSpots: "Puntos ciegos",
    blindSpotsLead: "Donde más te ves por encima de como te ven los demás.",
    globalIs: "global {value}.",
    youSeeYourself: "Tú te ubicas en {self} (brecha {gap}).",
    currentStep: "Hoy — {step}:",
    nextStep: "Siguiente peldaño — {step}:",
    kickerQualitative: "En sus palabras",
    qualitativeTitle: "Lo que escribieron",
    qualitativeLead:
      "La misma pregunta abierta para todos: qué aportas como líder y qué te ayudaría a crecer. Abajo está lo que dijeron, contado como lo contaría un coach — no las frases de cada quien.",
    supervisorWords: "Lo que dijo tu supervisor",
    supervisorWordsLead:
      "Esta respuesta se muestra tal cual la escribió y con su nombre, igual que su puntuación: no hay anonimato que proteger cuando responde una sola persona, y el cliente decidió que debías poder leerla.",
    ownWords: "Lo que escribiste sobre ti",
    noComments: "Todavía nadie ha escrito en esta sección.",
    kickerAction: "Plan de acción",
    reviewKicker: "Revisión del coach",
    reviewDraft: "Este reporte es un borrador",
    reviewDraftHelp:
      "Solo lo ven coaches y admins. Ni P&O ni la persona evaluada.",
    reviewReleased: "Aprobado y compartido con {name}",
    reviewReleasedHelp:
      "P&O puede consultarlo y la persona evaluada ya lo tiene disponible.",
    reviewApprove: "Aprobar reporte",
    reviewConfirm: "Sí, aprobar y compartir",
    reviewConfirmNote:
      "{name} podrá leer su reporte desde ese momento, y P&O podrá consultarlo. Se puede revertir.",
    reviewRevert: "Volver a borrador",
    actionPlanWrite: "Escribir el plan",
    actionPlanEdit: "Editar el plan",
    actionPlanSaved: "Guardado.",
    actionPlanPlaceholder:
      "Qué trabajar, en qué orden y con qué apoyo. Lo lee la persona evaluada.",
    actionPlanDraftNote: "Se guarda como borrador; no publica el reporte.",
    actionTitle: "Por dónde empezar",
    narrativePending: "Tu coach todavía no ha escrito esta sección.",
    narrativePendingHelp:
      "La lectura cualitativa y el plan de acción los redacta un coach a partir de estos números, y se revisan antes de compartir el reporte.",
    blocked:
      "Todavía no hay suficientes respuestas para publicar este reporte: hacen falta al menos {min} personas.",
    confidentialityNotice:
      "Las respuestas son anónimas: ningún grupo con menos de 3 respuestas se muestra por separado.",
    footerOwnership: "Este reporte es tuyo y de tu coach.",
    self: "Autoevaluación",
    others: "Global",
    gap: "Brecha",
    notPublished: "Tu reporte todavía no está disponible.",
    notPublishedHelp:
      "Un coach lo está revisando. Te avisamos en cuanto esté listo, al menos 3 días hábiles antes de tu sesión.",
    supervisorNotice:
      "La línea del supervisor corresponde a una sola persona y se muestra así a propósito; tu supervisor lo sabe.",
  },

  /**
   * La página del vídeo de introducción, pensada para pegarse en un mensaje.
   *
   * Habla al evaluador, que es a quien se dirige el vídeo: alguien a quien le
   * han pedido su mirada sobre colegas y todavía no sabe qué le van a pedir.
   */
  intro: {
    kicker: "Diagnóstico 360°",
    title: "Antes de responder, mira el video hasta el final.",
    lead: "Qué se te pide, cómo es la escala de tres niveles y por qué no es una nota. Lo que sale aquí es exactamente lo que verás al abrir tu enlace.",
    duration: "Dura 3 minutos y 38 segundos. Puedes verlo desde el móvil.",
    fallback: "Descargar el video",
  },

  errors: {
    generic: "Algo salió mal. Vuelve a intentarlo.",
    offline: "Sin conexión. Guardamos tu avance en cuanto vuelvas.",
    forbidden: "No tienes acceso a esta sección.",
    notFound: "No encontramos lo que buscabas.",
  },

  /**
   * El aviso de privacidad, en la voz del producto y no en la de un contrato.
   *
   * Quien lo abre está a punto de opinar sobre un colega, así que lo que
   * necesita no es una cláusula: es saber, en concreto, quién va a leer lo que
   * escriba. La versión completa para revisión legal vive en
   * `docs/aviso-de-privacidad.md`.
   */
  privacy: {
    link: "Aviso de privacidad",
    title: "Cómo se protege lo que respondes",
    subtitle: "Aviso de privacidad y confidencialidad · Diagnóstico 360°",
    back: "← Volver",

    summaryTitle: "En una frase",
    summary:
      "Tu valoración no llega con tu nombre. La persona que evalúas nunca sabe quién dijo qué, y la empresa tampoco: los resultados se presentan como promedios de grupo, y solo Genoma —la consultora externa que acompaña el programa— trabaja con ellos para preparar el desarrollo de cada líder.",
    summaryException:
      "Hay una excepción, decidida a propósito y avisada antes de responder: si evalúas a alguien de quien eres supervisor o supervisora, tu valoración se muestra por separado e identificada como tal.",

    collectedTitle: "Qué se recoge",
    collectedIdentity:
      "Tu nombre, cargo, área, correo, idioma y la relación que tienes con cada persona que evalúas, tal como los entregó la organización.",
    collectedAnswers:
      "Tus valoraciones en los 12 comportamientos —de 1 a 5, o “no lo he podido observar”— y el texto que escribas al final, que es opcional.",
    collectedProcess:
      "Si abriste la encuesta, si la enviaste y cuándo. Es avance operativo, hace posible el seguimiento, y nunca incluye nada de lo que respondiste.",

    whoTitle: "Quién ve qué",
    whoLead:
      "Esta tabla no describe lo que la pantalla oculta: describe lo que cada persona recibe. Un dato que no aparece aquí no viaja a ese lector.",
    whoColumnReader: "Quién",
    whoColumnSees: "Qué ve",
    whoColumnNever: "Qué no ve nunca",

    whoYou: "Tú, que evalúas",
    whoYouSees:
      "Tus propias respuestas, mientras respondes y hasta que envías.",
    whoYouNever: "Las respuestas de nadie más.",

    whoSubject: "La persona evaluada",
    whoSubjectSees:
      "Promedios por grupo, con un mínimo de 3 personas por grupo, y una síntesis de lo que se escribió sobre ella.",
    whoSubjectNever:
      "Ninguna respuesta individual, ningún texto tal como se escribió, y quiénes componen cada grupo.",

    whoCoach: "Su coach, de Genoma",
    whoCoachSees:
      "El reporte completo y las frases escritas, porque una síntesis que nadie puede contrastar con su origen no se puede revisar.",
    whoCoachNever:
      "Quién escribió cada frase. Le llegan agrupadas y barajadas, sin autor.",

    whoHr: "El equipo de P&O de la empresa",
    whoHrSees:
      "A quién se le pidió evaluar a quién, quién respondió y quién no, y las direcciones de correo.",
    whoHrNever:
      "Ningún reporte, ningún promedio, ninguna puntuación y ningún texto escrito por nadie.",

    whoHrNote:
      "Esa última fila es una decisión expresa de la organización: su propio equipo no lee los reportes. Conduce el ciclo y ve hasta dónde ha avanzado, pero no recibe sus resultados. Un equipo interno es el lector que más preocupa a quien responde un 360°, y la forma más corta de sostener la promesa es que no sea lector.",

    rulesTitle: "Las reglas que lo sostienen",
    rulesLead:
      "No son una declaración de intenciones. Son reglas aritméticas, y ninguna depende de que alguien se acuerde de aplicarla.",
    rule1:
      "Hacen falta al menos 3 personas distintas para que exista un reporte. Con menos, no se genera nada.",
    rule2:
      "Hacen falta al menos 3 respuestas para que un grupo —pares, reportes directos, stakeholders— se muestre por separado. Si no llega, se funde dentro del promedio global.",
    rule3:
      "Si sobrevive menos de un grupo, se muestra solo el global: un único grupo junto al global equivale a publicar el resto por descarte.",
    rule4:
      "No se publica cuántas personas hay detrás de cada promedio. Sin esos conteos, ningún grupo se puede despejar restando.",
    rulesComments:
      "Con los textos escritos la regla es todavía más estricta: nunca se separan por grupo, ni siquiera cuando las puntuaciones sí lo hacen. Saber que “esto lo escribió un par” reduce el campo de veinte personas a seis, y con un ejemplo concreto suele bastar.",

    supervisorTitle: "La excepción: el supervisor",
    supervisorBody:
      "Cuando evalúas a alguien de quien eres su supervisor o supervisora, tu puntuación se muestra en una línea propia, identificada como tal, y tu comentario se muestra tal como lo escribiste.",
    supervisorWhy:
      "Es una decisión de la organización, no un descuido: un supervisor es siempre una sola persona, así que su línea es su respuesta individual y no hay forma de anonimizarla. Quien recibe el reporte debe saber qué dijo quien lo dirige.",
    supervisorNotice:
      "Se te avisa antes de responder, no después. Y si en el mismo ciclo evalúas también a personas de las que eres par o stakeholder, esas respuestas sí son anónimas y se combinan con las demás.",

    commentsTitle: "Lo que escribes, y cómo llega",
    commentsStep1: "Escribes en tus palabras, con la extensión que quieras.",
    commentsStep2:
      "La persona evaluada no lee tu frase. Lee una síntesis de todo lo que se escribió sobre ella, con la prohibición explícita de citar, de mencionar un proyecto, una reunión, una fecha o un lugar, de decir de qué grupo viene una idea y de decir cuántas personas dijeron algo.",
    commentsStep3:
      "Un coach de Genoma la revisa y la aprueba antes de que nadie la lea. Sin esa aprobación, no se publica.",
    commentsStep4: "Si escribieron menos de 3 personas, no se muestra ninguna.",
    commentsAi:
      "La síntesis la redacta un modelo de lenguaje de Anthropic (Claude). Se le envían los textos y el nombre de pila de la persona evaluada; no se envía el nombre de quien escribió, ni su cargo, ni su relación, ni su correo. El modelo no recibe el dato que permitiría atribuir una frase, y nada de lo que produce se publica sin revisión humana.",

    residualTitle: "Lo que la organización sí sabe",
    residualLead:
      "Por honestidad, y porque un aviso que promete de más deja de servir justo cuando alguien lo revisa:",
    residual1:
      "El mapa del 360° lo definió la organización, así que sabe a quién se le pidió su opinión sobre quién. Lo que no sabe, ni puede deducir, es qué respondiste.",
    residual2:
      "El estado de tu encuesta es visible para P&O: si la abriste, si la enviaste y cuándo.",
    residual3:
      "En un grupo de exactamente 3 personas, quien conoce el mapa sabe qué tres personas forman ese promedio, aunque no cuál dijo qué —y P&O no ve ningún promedio en ninguna pantalla—. Es inherente a un 360° con mapa conocido y al umbral de 3.",

    rightsTitle: "Tus derechos, y con quién hablar",
    rightsBody:
      "Puedes pedir acceso a tus datos, corregirlos, oponerte a su tratamiento o revocar tu consentimiento escribiendo a {contact}. Retirar una respuesta ya enviada implica sacarla del promedio del que forma parte.",
    rightsSecurity:
      "El acceso a la encuesta es por enlace personal con vencimiento, las bases están aisladas por organización a nivel de motor de base de datos, y todo acceso a un reporte queda registrado.",
    rightsPurpose:
      "Este diagnóstico es una herramienta de desarrollo. No es una evaluación de desempeño.",

    contactEmail: "wecare@wearegenoma.com",
    footer:
      "Responsable del tratamiento: {controller} · Programa Leadership Accelerator.",
    controller: "Genoma",
  },
} as const;
